<template>
  <div class="place-search">
    <input
      v-if="config && !config.configured"
      type="text"
      disabled
      :id="id"
      :placeholder="$t('maps_not_configured')"
    />
    <GmapAutocomplete
      v-else-if="config && config.provider === 'google'"
      ref="input"
      :id="id"
      :placeholder="placeholder"
      @place_changed="onGooglePlace"
    />
    <template v-else-if="config && config.provider === 'mapbox'">
      <input
        ref="input"
        type="text"
        autocomplete="off"
        :id="id"
        :placeholder="placeholder"
        v-model="text"
        @input="onInput"
        @keydown.enter.prevent="selectPlace(results[0])"
        @keydown.esc="results = []"
        @blur="onBlur"
      />
      <!-- v-menu renders at the app root, so containers with overflow: hidden
           (e.g. the add-stop list group on the routes page) cannot clip it -->
      <v-menu
        v-if="inputEl"
        :value="results.length > 0"
        @input="onMenuInput"
        :activator="inputEl"
        :open-on-click="false"
        :close-on-content-click="false"
        disable-keys
        offset-y
        max-height="300"
      >
        <v-list>
          <template v-for="(result, index) in results">
            <v-divider v-if="index > 0" :key="'divider-' + result.place_id"></v-divider>
            <v-list-item
              :key="result.place_id"
              class="py-1"
              @mousedown.prevent
              @click="selectPlace(result)"
            >
              <v-list-item-content>
                <v-list-item-title class="mb-1">{{ result.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ result.formatted_address }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </template>
  </div>
</template>
<script>
import MapConfigService from "@/services/MapConfigService";
import { forwardGeocode } from "@/services/MapboxGeocoding";
import { googlePlaceToPlace } from "@/services/MapGeocoder";

// Address search input for the selected map provider. Emits "place-changed"
// with a provider-neutral place: { place_id, formatted_address, lat, lng }.
export default {
  props: {
    id: String,
    placeholder: String,
    // bias Mapbox results towards this {lat, lng}
    proximity: Object,
  },
  data() {
    return {
      config: null,
      text: "",
      results: [],
      searchTimer: null,
      inputEl: null,
    };
  },
  created() {
    MapConfigService.getConfig().then((config) => {
      this.config = config;
      // apply a value set before the input was rendered
      this.$nextTick(() => {
        this.setValue(this.text);
        this.inputEl = this.config.configured && this.config.provider === "mapbox" ? this.$refs.input : null;
      });
    });
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    inputElement() {
      const input = this.$refs.input;
      if (input == null) return null;
      return input.$el ? input.$el : input;
    },
    setValue(value) {
      this.text = value || "";
      const el = this.inputElement();
      if (el) el.value = this.text;
    },
    clear() {
      this.results = [];
      this.setValue("");
    },
    focus() {
      const el = this.inputElement();
      if (el) el.focus();
    },
    onGooglePlace(place) {
      const neutralPlace = googlePlaceToPlace(place);
      if (neutralPlace) {
        this.$emit("place-changed", neutralPlace);
      }
    },
    onInput() {
      clearTimeout(this.searchTimer);
      const text = this.text.trim();
      if (text.length < 3) {
        this.results = [];
        return;
      }
      this.searchTimer = setTimeout(() => {
        forwardGeocode(text, this.config, this.proximity)
          .then((results) => {
            // ignore responses for text the user already changed
            if (this.text.trim() === text) {
              this.results = results;
            }
          })
          .catch((e) => console.log(e));
      }, 300);
    },
    onBlur() {
      this.results = [];
    },
    onMenuInput(open) {
      if (!open) {
        this.results = [];
      }
    },
    selectPlace(place) {
      if (place == null) return;
      this.results = [];
      this.setValue(place.formatted_address);
      this.$emit("place-changed", place);
    },
  },
};
</script>
<style scoped>
.place-search {
  position: relative;
  width: 100%;
}
.place-search input {
  width: 100%;
}
</style>
